import { tagName } from "@ember-decorators/component"
import Component from "@ember/component"
import { computed } from "@ember/object"
import PromiseProxyMixin from "@ember/object/promise-proxy-mixin"
import ObjectProxy from "@ember/object/proxy"
import { inject as service } from "@ember/service"
import type { Permission } from "@shared/models/permissions"
import type { Planbook } from "@shared/models/planbook"
import type { TypeToModel } from "@shared/models/type-to-model"
import findMany from "cc-frontend/helpers/find-many"
import type EmberStore from "cc-frontend/services/ember-store"
import type PhantomCardStacks from "cc-frontend/services/phantom-card-stacks"
import { Promise } from "rsvp"

class ObjectPromiseProxy extends ObjectProxy.extend(PromiseProxyMixin) {}

// With the 4 different forks this can take, it's useful to log all the options when debugging
// Set this to true to see what's up.
const LOG_STATES = false

@tagName("")
export default class CardStackProvider extends Component {
  @service phantomCardStacks!: PhantomCardStacks
  @service("ember-store") store!: EmberStore

  // Arguments
  planbook!: Planbook
  dateString!: string
  cardStackId!: string | null
  phantomCardStackId!: string | null
  cardStackTemplateId!: string | null
  unitIds?: Array<string> | null
  useCardStackSummary?: boolean = false
  useStudentVersion?: boolean = false
  usedAs!: "routine" | "lesson"
  ownerId: string | null = null
  defaultPermissions: Array<Permission> | [] = []
  relationships: Array<[string, string]> | [] = []

  // Properties
  lastProxy: any | null = null
  lastCardStackId: string | null = null
  lastReceivedCardStackId: string | null = null
  lastPhantomCardStackId: string | null = null
  isShowingPhantomCardStack!: boolean
  lastDateString: string | null = null

  log(...args) {
    console.log(`CSPROVIDER: ${this.usedAs}:${this.dateString}`, ...args)
  }

  @findMany("unitIds", "card-stack-summary") units

  /**
   * Either return the actual lesson or a phantom lesson
   * We watch the dateString b/c we might have a bunch of null cardStackIds and the datestring triggers
   * the revalidation.
   */
  @computed("cardStackId", "cardStackTemplateId", "dateString", "phantomCardStackId")
  get cardStack() {
    // If we had a cardStack and now we don't (e.g. we moved the lesson that was in this place)
    // we reset all the caches.
    if (LOG_STATES)
      this.log(this.ownerId, {
        cardStackId: this.cardStackId,
        lastCardStackId: this.lastCardStackId,
        lastReceivedCardStackId: this.lastReceivedCardStackId,
        lastPhantomCardStackId: this.lastPhantomCardStackId,
      })
    // I think this is all the ways we'd want to invalidate this. BUT I had like a gajillion
    // cases before (commented out) so if we get bugs, see if I'm missing something
    if (
      // We were using a phantom card stack but moved something in
      (this.isShowingPhantomCardStack === true &&
        this.cardStackId !== this.lastPhantomCardStackId &&
        this.phantomCardStackId !== this.lastPhantomCardStackId) ||
      // We moved a card stack out
      (this.isShowingPhantomCardStack === false &&
        this.cardStackId === null &&
        this.lastCardStackId !== null) ||
      // We moved a new card stack into this place
      (this.isShowingPhantomCardStack === false && this.cardStackId !== this.lastCardStackId) ||
      // We changed the card stack id
      (this.isShowingPhantomCardStack === false &&
        this.cardStackId !== this.lastReceivedCardStackId)

      // (this.cardStackId === null && this.lastCardStackId !== null) || // we moved a card stack out.
      // this.cardStackId !== this.lastCardStackId || // we mvoed a new card stack into this place
      // (this.cardStackId === null && // we're dealing with a case where we need the phantom card id
      // this.phantomCardStackId !== undefined && // we actually passed a phantom card stack id in
      //   this.phantomCardStackId !== this.lastPhantomCardStackId) // it's not the same as last time
    ) {
      if (LOG_STATES)
        this.log("IS_INVALIDATING", this.dateString, this.cardStackId, this.lastCardStackId)
      this.lastCardStackId = null
      this.lastProxy = null
      this.lastDateString = null
      if (this.ownerId === null || this.dateString === null)
        throw Error("Missing ownerId and dateString")
      this.phantomCardStacks.invalidateDate(this.ownerId, this.cardStackId, this.dateString)
    }
    this.lastPhantomCardStackId = this.phantomCardStackId
    this.lastReceivedCardStackId = this.cardStackId
    return this.cardStackId ? this._returnExistingCardStack() : this._returnPhantomCardStack()
  }

  /**
   * If we've already returned it, we'll just return the proxy
   * If we haven't returned anything yet, we look it up in the store
   */
  _returnExistingCardStack() {
    if (this.cardStackId === null) return
    this.isShowingPhantomCardStack = false
    this.lastPhantomCardStackId = null
    // This means the coursedate has a lesson. If it's a lesson we created in the branch
    // below, we want to just return that so things don't jump around the page.
    if (this.lastCardStackId === this.cardStackId) {
      // If we're hitting this path, something changed and it was probably the
      // template id. So, we check if we need to update the template
      if (this.useCardStackSummary !== true) {
        this.phantomCardStacks.applyTemplateCardsToCardStackIfNeeded(
          this.cardStackId,
          this.cardStackTemplateId
        )
      }
      // Return the last proxy
      if (LOG_STATES) this.log("RETURN_EXISTING_CARD_STACK__LAST_PROXY")
      return this.lastProxy
    } else {
      // Find the cardStack
      let model: keyof TypeToModel = this.useCardStackSummary ? "card-stack-summary" : "card-stack"
      model = this.useStudentVersion ? "class-website-lesson" : model
      let promise = this.store.find(model, this.cardStackId, false)

      // Check if it needs cards
      if (this.useCardStackSummary !== true) {
        promise.then((_cardStack) => {
          if (this.isDestroyed || this.isDestroying) return
          this.phantomCardStacks.applyTemplateCardsToCardStackIfNeeded(
            this.cardStackId,
            this.cardStackTemplateId
          )
        })
      }

      // Cache the date
      this.lastDateString = this.dateString
      // cache the card stack id
      this.lastCardStackId = this.cardStackId
      // cache the proxy
      if (LOG_STATES) this.log("RETURN_EXISTING_CARD_STACK__NEW_PROXY", this.dateString)
      return (this.lastProxy = ObjectPromiseProxy.create({ promise: promise }))
    }
  }

  /**
   * If we've returned soemthing before, we return it again
   * If we haven't, we request the service for one
   */
  _returnPhantomCardStack() {
    if (this.ownerId === null) return null
    this.isShowingPhantomCardStack = true

    // If we've already returned a proxy, we'll just return it again
    // but we'll check if we should update the cards if needed
    //
    if (this.lastProxy && this.lastDateString === this.dateString) {
      if (this.useCardStackSummary !== true) {
        this.phantomCardStacks.applyTemplateCardsToCardStackIfNeeded(
          this.lastCardStackId,
          this.cardStackTemplateId
        )
      }
      if (LOG_STATES) this.log("RETURN_PHANTOM__LAST_PROXY", this.dateString)
      return this.lastProxy
    }

    // We'll find a card stack for the date. The service caches the card stacks
    // so the same one gets returned for the same date and course/planbook
    let cardStack = this.phantomCardStacks.findCardStackForDate(
      this.ownerId,
      this.usedAs,
      this.dateString,
      this.defaultPermissions,
      this.relationships,
      !!this.planbook.attributes.flags.unifiedLessons,
      this.useCardStackSummary || false
    )

    // Apply the template cards if needed
    if (this.useCardStackSummary !== true) {
      this.phantomCardStacks.applyTemplateCardsToCardStackIfNeeded(
        cardStack.id,
        this.cardStackTemplateId
      )
    }

    // Cache the date string so we can tell phantom card stacks from each other
    this.lastDateString = this.dateString
    // Cache the id that's returned to us
    this.lastCardStackId = cardStack.id
    this.lastPhantomCardStackId = cardStack.id
    // Cache the promise and return it
    if (LOG_STATES)
      this.log("RETURN_PHANTOM__NEW_PROXY", this.dateString, this.usedAs, this.toString())
    return (this.lastProxy = ObjectPromiseProxy.create({ promise: Promise.resolve(cardStack) }))
  }
}
